import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DatePicker }           from '@mui/x-date-pickers'
import { AdapterMoment }        from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers'
import moment                   from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { shallowEqual } from 'react-redux'
import {
  Box, Button, Checkbox, ClickAwayListener, Divider, FormControl,
  InputAdornment, MenuItem, Paper, Popper,
  Select, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import SearchIcon      from '@mui/icons-material/Search'
import {
  selectBeacon4Filters,
  selectBeacon4SelectedTechs,
  setBeacon4Aggregation, setBeacon4Side,
  setBeacon4Polygon, setBeacon4Jvid,
  setBeacon4DateFrom, setBeacon4DateTo,
  setBeacon4SelectedTechs,
  loadBeacon4SidePolygons,
} from '../../reducers/Beacon4FiltersReducer'
import { reloadBeacon4View } from '../../actions/Beacon4PerformanceActions'

const AGG_OPTIONS = ['Daily', 'Weekly', 'Monthly']

function Label({ children }) {
  return (
    <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mb: '3px' }}>
      {children}
    </Typography>
  )
}

// ── MultiSelectDropdown ──────────────────────────────────────────────────────
const MultiSelectDropdown = memo(function MultiSelectDropdown({ label, value = '', options = [], onChange, disabled }) {
  const [open, setOpen]     = useState(false)
  const [search, setSearch] = useState('')
  const anchorRef           = useRef(null)
  const onChangeRef         = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange }, [onChange])

  const selectedSet = useMemo(() => new Set(typeof value === 'string' ? value.split(',').filter(Boolean) : value), [value])
  const filtered    = useMemo(() => options.filter(o => o.toLowerCase().includes(search.toLowerCase())), [options, search])

  const toggle          = useCallback((opt) => {
    const next = new Set(selectedSet)
    next.has(opt) ? next.delete(opt) : next.add(opt)
    onChangeRef.current([...next].join(','))
  }, [selectedSet])

  const handleSelectAll = useCallback(() => { onChangeRef.current(filtered.join(',')) }, [filtered])
  const handleClear     = useCallback(() => { onChangeRef.current('') }, [])
  const handleClose     = useCallback(() => { setOpen(false); setSearch('') }, [])
  const selected        = useMemo(() => [...selectedSet], [selectedSet])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Label>{label}</Label>
      <Box ref={anchorRef}>
        <Button
          variant="outlined" size="small" disabled={disabled}
          onClick={() => !disabled && setOpen(v => !v)}
          sx={{
            height: 28, fontSize: 11, minWidth: 130, maxWidth: 200,
            textTransform: 'none', justifyContent: 'space-between',
            color: selected.length ? 'text.primary' : 'text.disabled',
            borderColor: 'divider', bgcolor: 'background.default',
            '&:hover': { borderColor: 'primary.main' },
            overflow: 'hidden',
          }}
          endIcon={<Typography sx={{ fontSize: 10, ml: 0.5 }}>▾</Typography>}
        >
          <Typography noWrap sx={{ fontSize: 11, maxWidth: 150 }}>
            {selected.length === 0 ? '(All)' : selected.length === 1 ? selected[0] : `${selected.length} selected`}
          </Typography>
        </Button>
      </Box>

      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1400 }}>
        <ClickAwayListener onClickAway={handleClose}>
          <Paper elevation={4} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', minWidth: 200, maxWidth: 260 }}>
            <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <TextField
                size="small" fullWidth autoFocus placeholder="Search..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: 'text.disabled' }} /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { height: 26, fontSize: 11 } }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Button size="small" sx={{ fontSize: 10, textTransform: 'none', py: 0 }} onClick={handleClear}>Clear</Button>
              <Button size="small" sx={{ fontSize: 10, textTransform: 'none', py: 0 }} onClick={handleSelectAll}>Select all</Button>
            </Box>
            <Box sx={{ maxHeight: 220, overflowY: 'auto', py: 0.5 }}>
              {filtered.length === 0 ? (
                <MenuItem disabled sx={{ fontSize: 11 }}>No results</MenuItem>
              ) : filtered.map(opt => (
                <Box key={opt} onClick={() => toggle(opt)}
                  sx={{ display: 'flex', alignItems: 'center', px: 1, py: '2px', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                  <Checkbox checked={selectedSet.has(opt)} size="small" sx={{ p: 0.5 }} disableRipple />
                  <Typography sx={{ fontSize: 11 }}>{opt}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  )
})

// ── FilterSelect ─────────────────────────────────────────────────────────────
function FilterSelect({ label, value, options = [], onChange, disabled }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {label && <Label>{label}</Label>}
      <FormControl size="small" disabled={disabled}>
        <Select
          value={value ?? ''} onChange={e => onChange(e.target.value)} displayEmpty
          sx={{ height: 28, fontSize: 11, minWidth: 110, bgcolor: 'background.default', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}
        >
          <MenuItem value="" sx={{ fontSize: 11 }}>(All)</MenuItem>
          {options.map(o => <MenuItem key={o} value={o} sx={{ fontSize: 11 }}>{o}</MenuItem>)}
        </Select>
      </FormControl>
    </Box>
  )
}

// ── FilterBar ────────────────────────────────────────────────────────────────
export default function Beacon4FilterBar({ exportButton }) {
  const dispatch      = useDispatch()
  const { aggregation, dateFrom, dateTo, side, polygon, jvid, sides, polygons, jvids } = useSelector(selectBeacon4Filters, shallowEqual)
  const canApply = !!(dateFrom && dateTo && dateFrom <= dateTo)

  useEffect(() => { dispatch(loadBeacon4SidePolygons()) }, []) // eslint-disable-line

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Paper elevation={0} square sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, px: 2.5, pt: 1, pb: 0.75, flexWrap: 'wrap' }}>

          {/* Date range */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Label>First Day</Label>
            <DatePicker
              format="YYYY-MM-DD"
              value={dateFrom ? moment(dateFrom, 'YYYY-MM-DD') : null}
              onChange={v => dispatch(setBeacon4DateFrom(v?.isValid() ? v.format('YYYY-MM-DD') : ''))}
              maxDate={dateTo ? moment(dateTo, 'YYYY-MM-DD').subtract(1, 'day') : undefined}
              slotProps={{ textField: { size: 'small', sx: { width: 145, '& .MuiOutlinedInput-root': { height: 28, fontSize: 11 } } } }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Label>Last Day</Label>
            <DatePicker
              format="YYYY-MM-DD"
              value={dateTo ? moment(dateTo, 'YYYY-MM-DD') : null}
              onChange={v => dispatch(setBeacon4DateTo(v?.isValid() ? v.format('YYYY-MM-DD') : ''))}
              minDate={dateFrom ? moment(dateFrom, 'YYYY-MM-DD').add(1, 'day') : undefined}
              slotProps={{ textField: { size: 'small', sx: { width: 145, '& .MuiOutlinedInput-root': { height: 28, fontSize: 11 } } } }}
            />
          </Box>

          {/* Side */}
          <FilterSelect label="Side" value={side} options={sides} onChange={v => dispatch(setBeacon4Side(v))} />

          {/* Polygon */}
          <MultiSelectDropdown
            label="Polygon"
            value={polygon}
            options={polygons}
            onChange={v => dispatch(setBeacon4Polygon(v))}
            disabled={!side}
          />

          {/* JV ID */}
          <MultiSelectDropdown
            label="JV ID"
            value={jvid}
            options={jvids}
            onChange={v => dispatch(setBeacon4Jvid(v))}
            disabled={!side}
          />

          {/* Aggregation */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Label>Aggregation</Label>
            <ToggleButtonGroup exclusive value={aggregation} size="small"
              onChange={(_, v) => { if (v) dispatch(setBeacon4Aggregation(v)) }}
              sx={{ height: 28 }}>
              {AGG_OPTIONS.map(a => (
                <ToggleButton key={a} value={a} sx={{
                  px: 1.25, fontSize: 11, fontWeight: 500, textTransform: 'none',
                  '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
                }}>{a}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Apply */}
          <Button variant="contained" size="small" onClick={() => dispatch(reloadBeacon4View())} disabled={!canApply}
            sx={{ height: 28, alignSelf: 'flex-end', px: 2.5 }}>
            Apply
          </Button>

          {/* Export */}
          {exportButton && (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {exportButton}
            </Box>
          )}

        </Box>
      </Paper>
    </LocalizationProvider>
  )
}